import React from "react";

import { getProfileThumbnail } from "@shared/hooks/useProfile";
import theme from "@shared/utils/themeColors";
import { useConversations } from "../context/ConversationsContext";

interface DirectMessagesProps {
  setMsgSidebarOpen: (open: boolean) => void;
  selectedConversation: string;
  setSelectedConversation: React.Dispatch<React.SetStateAction<string>>;
}

const DirectMessages: React.FC<DirectMessagesProps> = ({
  setMsgSidebarOpen,
  selectedConversation,
  setSelectedConversation,
}) => {
  const {
    conversations,
    loading: conversationsLoading,
    userProfiles,
    getOrCreateConversation,
  } = useConversations();

  return (
    <div className="mt-4">
      <div
        className={`text-xs font-semibold text-gray-400 dark: ${theme.text.muted}`}
      >
        Direct messages
      </div>
      <ul className="mb-6">
        {conversationsLoading ? (
          <div className="flex items-center justify-center h-20">
            <div
              className={`animate-spin rounded-full h-8 w-8 border-b-2 ${theme.border.brand}`}
            ></div>
          </div>
        ) : (
          Object.values(userProfiles).map((otherUser) => {
            const conversation = conversations.find((conv) =>
              conv.participants.includes(otherUser.id)
            );

            return (
              <li className="-mx-2" key={otherUser.id}>
                <button
                  className={`flex items-center justify-between w-full p-2 rounded-lg ${
                    selectedConversation === conversation?.id
                      ? `bg-[linear-gradient(135deg,var(--tw-gradient-stops))] ${theme.bg.gradientPrimary}`
                      : "hover:bg-slate-50 dark:hover:bg-slate-800"
                  }`}
                  onClick={() => {
                    setMsgSidebarOpen(false);
                    if (conversation) {
                      setSelectedConversation(conversation.id);
                    } else {
                      getOrCreateConversation(otherUser.id).then(
                        (conversationId) => {
                          if (conversationId) {
                            setSelectedConversation(conversationId);
                          }
                        }
                      );
                    }
                  }}
                >
                  <div className="flex items-center truncate">
                    <img
                      className="w-8 h-8 rounded-full mr-2"
                      src={getProfileThumbnail(otherUser)}
                      width="32"
                      height="32"
                      alt={otherUser.name}
                    />
                    <div>
                      <div className="truncate text-left">
                        <span
                          className={`text-sm font-medium ${theme.text.primary}`}
                        >
                          {otherUser.name}
                        </span>
                      </div>
                      {conversation?.lastMessage && (
                        <div className="text-sm text-left text-slate-500 dark:text-slate-400 truncate">
                          {conversation.lastMessage.content}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center ml-2">
                    {conversation?.unreadCount > 0 && (
                      <div
                        className={`text-xs inline-flex font-medium ${theme.bg.brand} ${theme.text.primary} rounded-full text-center leading-5 px-2`}
                      >
                        {conversation?.unreadCount}
                      </div>
                    )}
                  </div>
                </button>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
};

export default DirectMessages;
